import { useState } from 'preact/hooks';
import { useAppDispatch, useAppSelector } from '../hooks';
import { createOffer } from '../store/offersSlice';
import LoadingOverlay from './LoadingOverlay';
import type { IAddSuggestionModalProps } from '../types';

export default function AddSuggestionModal({ isOpen, onClose }: IAddSuggestionModalProps) {
    const dispatch = useAppDispatch();
    const loading = useAppSelector(s => s.offers.loading);
    const refs = useAppSelector(s => s.references.data);
    const [offerId, setOfferId] = useState('');
    const [vendorCode, setVendorCode] = useState('');
    const [preVendorName, setPreVendorName] = useState('');
    const [buyerCode, setBuyerCode] = useState('');
    const [dat, setDat] = useState('');
    const [datSupply, setDatSupply] = useState('');
    const [datPayment, setDatPayment] = useState('');
    const [nds, setNds] = useState('');
    const [typeOffer, setTypeOffer] = useState('0');
    const [countryCode, setCountryCode] = useState('');
    const [offerBrand, setOfferBrand] = useState('');

    const handleSubmit = async (e: Event) => {
        e.preventDefault();

        await dispatch(createOffer({
            offer_id: offerId || null,
            vendor_code: vendorCode ? Number(vendorCode) : null,
            pre_vendor_name: preVendorName || null,
            buyer_code: buyerCode ? Number(buyerCode) : null,
            dat: dat || null,
            dat_supply: datSupply || null,
            dat_payment: datPayment || null,
            nds: nds ? Number(nds) : null,
            type_offer: Number(typeOffer) as 0 | 1,
            country_guid: countryCode || null,
            offer_brand: offerBrand || null,
        }));

        resetForm();
        onClose();
    };

    const resetForm = () => {
        setOfferId(''); setVendorCode(''); setPreVendorName('');
        setBuyerCode(''); setDat(''); setDatSupply(''); setDatPayment('');
        setNds(''); setTypeOffer('0'); setCountryCode(''); setOfferBrand('');
    };

    if (!isOpen) return null;

    const firms = refs?.firms ?? [];
    const countries = refs?.countries ?? [];
    const buyers = refs?.buyers ?? [];


    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-lg relative">
                <LoadingOverlay visible={loading} />
                <div className="p-6 border-b border-base-300">
                    <h2 className="text-xl font-bold">Создать предложение</h2>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                    <div>
                        <label className="block text-sm font-medium mb-1">Номер предложения</label>
                        <input type="text" className="input input-bordered w-full" value={offerId}
                            onChange={e => setOfferId(e.currentTarget.value)} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Бренд предложения *</label>
                        <input type="text" className="input input-bordered w-full" value={offerBrand}
                            onChange={e => setOfferBrand(e.currentTarget.value)} required />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Поставщик</label>
                        <select className="select select-bordered w-full" value={vendorCode}
                            onChange={e => setVendorCode((e.target as HTMLSelectElement).value)}>
                            <option value="">Выберите или введите вручную</option>
                            {firms.map(f => (
                                <option key={f.code} value={f.code}>{f.name}</option>
                            ))}
                        </select>
                    </div>
                    {!vendorCode && (
                        <div>
                            <label className="block text-sm font-medium mb-1">Поставщик (вручную)</label>
                            <input type="text" className="input input-bordered w-full" value={preVendorName}
                                onChange={e => setPreVendorName(e.currentTarget.value)} />
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium mb-1">Байер</label>
                        <select className="select select-bordered w-full" value={buyerCode}
                            onChange={e => setBuyerCode((e.target as HTMLSelectElement).value)}>
                            <option value="">—</option>
                            {buyers.map(b => (
                                <option key={b.code} value={b.code}>{b.buyer_name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Тип</label>
                            <select className="select select-bordered w-full" value={typeOffer}
                                onChange={e => setTypeOffer((e.target as HTMLSelectElement).value)}>
                                <option value="0">Локал</option>
                                <option value="1">Импорт</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">НДС</label>
                            <input type="number" className="input input-bordered w-full" value={nds}
                                onChange={e => setNds(e.currentTarget.value)} />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Страна отгрузки</label>
                        <select className="select select-bordered w-full" value={countryCode}
                            onChange={e => setCountryCode((e.target as HTMLSelectElement).value)}>
                            <option value="">—</option>
                            {countries.map(c => (
                                <option key={c.guid || c.code} value={c.guid || c.code}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Дата предл.</label>
                            <input type="date" className="input input-bordered w-full" value={dat}
                                onChange={e => setDat(e.currentTarget.value)} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Дата поставки</label>
                            <input type="date" className="input input-bordered w-full" value={datSupply}
                                onChange={e => setDatSupply(e.currentTarget.value)} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Дата оплаты</label>
                            <input type="date" className="input input-bordered w-full" value={datPayment}
                                onChange={e => setDatPayment(e.currentTarget.value)} />
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t border-base-300">
                        <button type="button" className="btn btn-ghost" onClick={() => { resetForm(); onClose(); }}>Отмена</button>
                        <button type="submit" className="btn btn-accent">Создать</button>
                    </div>
                </form>
            </div>
        </div>
    );
}
